import { Head, Link, router, usePage } from "@inertiajs/react"
import { toQueryString } from "@/lib/queryParams"
import { useTableDensity } from "@/lib/useTableDensity"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import DownloadIcon from "@mui/icons-material/Download"
import EditIcon from "@mui/icons-material/Edit"
import HistoryIcon from "@mui/icons-material/History"
import RefreshIcon from "@mui/icons-material/Refresh"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import RecordHistoryDrawer from "@/pages/history/RecordHistoryDrawer"
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material"
import dayjs from "dayjs"
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMaterialReactTable,
} from "material-react-table"
import { ElementType, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type SortDirection = "asc" | "desc"

interface IExport {
    id: number
    name: string
    client_name: string
    update_date: string
    updated_at: string
    entries_count: number
}

type Props = {
    entries: IExport[]
    search: string
    sort: string
    direction: SortDirection
    pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
}

function DeleteDialog({ entry, onClose, tableParams }: {
    entry: IExport | null
    onClose: () => void
    tableParams: Record<string, string | number>
}) {
    const { t } = useTranslation()
    const [processing, setProcessing] = useState(false)
    const [snapshot, setSnapshot] = useState<IExport | null>(null)

    const handleDelete = () => {
        if (!snapshot) return
        setProcessing(true)
        const query = new URLSearchParams(tableParams as Record<string, string>).toString()
        router.delete(`/exports/${snapshot.id}?${query}`, {
            preserveScroll: true,
            onFinish: () => { setProcessing(false); onClose() },
        })
    }

    return (
        <Dialog open={entry !== null} onClose={onClose} maxWidth="xs" fullWidth
            slotProps={{ transition: { onEnter: () => setSnapshot(entry) } }}>
            <DialogTitle>{t("export.deleteTitle")}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t("export.deleteText", { name: snapshot?.name })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose} disabled={processing} size="small">{t("common.cancel")}</Button>
                <Button color="error" variant="contained" size="small" onClick={handleDelete} disabled={processing}>
                    {processing ? t("common.deleting") : t("common.delete")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default function ExportsIndex() {
    const { entries, search: initialSearch, sort, direction, pagination } = usePage<Props>().props
    const { t } = useTranslation()

    const [deleteEntry, setDeleteEntry] = useState<IExport | null>(null)
    const [historyTarget, setHistoryTarget] = useState<{ type: string; id: number } | null>(null)
    const [reloading, setReloading] = useState(false)
    const { density, onDensityChange } = useTableDensity("exports")
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const defaultSort = sort ?? "update_date"
    const defaultDirection = direction ?? "desc"

    const [sorting, setSorting] = useState<MRT_SortingState>(
        [{ id: defaultSort, desc: defaultDirection === "desc" }]
    )
    const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
        pageIndex: (pagination?.current_page ?? 1) - 1,
        pageSize: pagination?.per_page ?? 25,
    })
    const [globalFilter, setGlobalFilter] = useState(initialSearch ?? "")

    useEffect(() => { setGlobalFilter(initialSearch ?? "") }, [initialSearch])
    useEffect(() => { setSorting([{ id: sort ?? "update_date", desc: (direction ?? "desc") === "desc" }]) }, [sort, direction])
    useEffect(() => {
        setPaginationState({ pageIndex: (pagination?.current_page ?? 1) - 1, pageSize: pagination?.per_page ?? 25 })
    }, [pagination?.current_page, pagination?.per_page])

    useEffect(() => {
        const key = "scroll_exports"
        const saved = sessionStorage.getItem(key)
        if (saved) {
            sessionStorage.removeItem(key)
            requestAnimationFrame(() => { window.scrollTo({ top: parseInt(saved) }) })
        }
        return router.on("before", () => { sessionStorage.setItem(key, String(window.scrollY)) })
    }, [])

    const visit = useCallback((params: Record<string, string | number>) => {
        router.get("/exports", params, { preserveState: true, preserveScroll: true })
    }, [])

    const tableParams = useMemo(() => ({
        search: globalFilter,
        sort: sorting[0]?.id ?? "update_date",
        direction: (sorting[0]?.desc ? "desc" : "asc") as SortDirection,
        page: paginationState.pageIndex + 1,
        per_page: paginationState.pageSize,
    }), [globalFilter, sorting, paginationState])

    const columns = useMemo<MRT_ColumnDef<IExport>[]>(() => [
        { accessorKey: "name", header: t("export.form.name"), size: 220 },
        { accessorKey: "client_name", header: t("export.form.client"), size: 200 },
        {
            accessorKey: "update_date",
            header: t("export.form.updateDate"),
            size: 140,
            Cell: ({ cell }) => cell.getValue<string>() ? dayjs(cell.getValue<string>()).format("DD-MM-YYYY") : "—",
        },
        {
            accessorKey: "entries_count",
            header: t("export.entriesCount"),
            size: 100,
            muiTableBodyCellProps: { align: "center" },
        },
        {
            accessorKey: "updated_at",
            header: t("common.updatedAt"),
            size: 160,
            Cell: ({ cell }) => dayjs(cell.getValue<string>()).format("DD-MM-YYYY HH:mm"),
        },
    ], [t])

    const table = useMaterialReactTable({
        columns,
        data: entries ?? [],
        enableRowActions: true,
        positionActionsColumn: "last",
        enableColumnFilters: false,
        enableFullScreenToggle: false,
        initialState: { showGlobalFilter: !!initialSearch },
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        rowCount: pagination?.total_count ?? 0,
        onGlobalFilterChange: (updater) => {
            const val = typeof updater === "function" ? updater(globalFilter) : (updater ?? "")
            setGlobalFilter(val)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                visit({
                    search: val,
                    sort: sorting[0]?.id ?? "update_date",
                    direction: sorting[0]?.desc ? "desc" : "asc",
                    page: 1,
                    per_page: paginationState.pageSize,
                })
            }, 400)
        },
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater(paginationState) : updater
            setPaginationState(next)
            visit({
                search: globalFilter,
                sort: sorting[0]?.id ?? "update_date",
                direction: sorting[0]?.desc ? "desc" : "asc",
                page: next.pageIndex + 1,
                per_page: next.pageSize,
            })
        },
        onSortingChange: (updater) => {
            const next = typeof updater === "function" ? updater(sorting) : updater
            setSorting(next)
            visit({
                search: globalFilter,
                sort: next[0]?.id ?? "update_date",
                direction: next[0]?.desc ? "desc" : "asc",
                page: 1,
                per_page: paginationState.pageSize,
            })
        },
        muiPaginationProps: {
            rowsPerPageOptions: [25, 50, 100, 200],
            showFirstButton: true,
            showLastButton: true,
        },
        renderRowActions: ({ row }) => {
            const editUrl = `/exports/${row.original.id}/edit?${toQueryString(tableParams)}`
            return (
                <Box sx={{ display: "flex" }}>
                    <Tooltip title={t("common.edit")}>
                        <IconButton color="info" size="small" component={Link as ElementType} href={editUrl}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.downloadExcel")}>
                        <IconButton color="success" size="small" component="a" href={`/exports/${row.original.id}/download_excel`}>
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("history.openRecordHistory")}>
                        <IconButton size="small" onClick={() => setHistoryTarget({ type: "Export", id: row.original.id })}>
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                        <IconButton color="error" size="small" onClick={() => setDeleteEntry(row.original)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
        onDensityChange,
        state: {
            showProgressBars: reloading,
            pagination: paginationState,
            globalFilter,
            sorting,
            density,
        },
    })

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Head title={t("modules.exports")} />

            <Box sx={{ mb: 2 }}>
                <Button LinkComponent={Link} href="/" size="small" startIcon={<ArrowBackIcon />} color="inherit">
                    {t("common.home")}
                </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("modules.exports")}</Typography>
                    <Tooltip title={t("common.reload")}>
                        <IconButton size="small" onClick={() => { setReloading(true); router.reload({ onFinish: () => setReloading(false) }) }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button variant="outlined" LinkComponent={Link} href="/orders" size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ color: "text.primary", borderColor: "text.primary" }}>
                        {t("modules.orders")}
                    </Button>
                    <Button variant="outlined" LinkComponent={Link} href="/extrusions" size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ color: "text.primary", borderColor: "text.primary" }}>
                        {t("modules.extrusion")}
                    </Button>
                    <Button
                        LinkComponent={Link}
                        href="/history?module=exports"
                        startIcon={<HistoryIcon />}
                        variant="outlined"
                        size="small"
                    >
                        {t("history.openHistory")}
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} LinkComponent={Link} href="/exports/new" size="small">
                        {t("export.new")}
                    </Button>
                </Box>
            </Box>

            <MaterialReactTable table={table} />

            <DeleteDialog entry={deleteEntry} onClose={() => setDeleteEntry(null)} tableParams={tableParams} />
            <RecordHistoryDrawer
                open={historyTarget !== null}
                onClose={() => setHistoryTarget(null)}
                type={historyTarget?.type ?? null}
                id={historyTarget?.id ?? null}
            />
        </Container>
    )
}
